import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../../helpers/reselectHelpers';
import {getFlatPanels, getPanels} from '../../panelsSelectors';
import {panelIsContactsManagerPanel} from '../../panelsState';
import {ContactsManagerPanelId, PanelTypesEnum} from '../../panelStateBase';
import {ContactsManagerPanelState} from './contactsManagerPanelState';
import {fileStateIsAccountsCSVFile, fileStateIsContactsCsvFile} from './file/contactsManagerFileState';
import {ContactsManagerParametersTypesEnum} from './parameters/contactsManagerParameters';
import {sidePanelIsCreateContactForm} from './sidePanel/contactsManagerSidePanelState';
import {
  tableStateIsAccountContactsTableState,
  tableStateIsAccountsTableState,
  tableStateIsContactsTableState,
  tableStateIsListsTableState,
} from './table/contactsManagerTableState';

/*
 * Panel selectors.
 */

/** @deprecated Use makeGetContactsManagerPanel() to obtain a parameterized selector instead. */
export const getContactsManagerPanel = createSelector(getFlatPanels, (panels) => {
  const panel = panels.find(panelIsContactsManagerPanel);
  if (!panel || panel.type !== PanelTypesEnum.CONTACTS_MANAGER) {
    return undefined;
  }

  return panel;
});

export interface ContactsManagerPanelProps {
  panelId: ContactsManagerPanelId;
}

export type ContactsManagerPanelIdSelector<P> = AppParametricSelector<P, ContactsManagerPanelId>;

function getContactsManagerPanelId(_state: unknown, props: ContactsManagerPanelProps) {
  return props.panelId;
}

export type ContactsManagerPanelSelector<P> = AppParametricSelector<P, ContactsManagerPanelState>;

export function makeGetContactsManagerPanel(): ContactsManagerPanelSelector<ContactsManagerPanelProps>;
export function makeGetContactsManagerPanel<P>(
  getPanelId: ContactsManagerPanelIdSelector<P>,
): ContactsManagerPanelSelector<P>;
export function makeGetContactsManagerPanel(
  getPanelId: ContactsManagerPanelIdSelector<any> = getContactsManagerPanelId,
) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.CONTACTS_MANAGER) {
      throw new Error('Contacts manager panel not found.');
    }

    return panel;
  });
}

export function makeGetContactsManagerPath() {
  return createSelector(makeGetContactsManagerPanel(), ({parameters}) => parameters.location.pathname);
}

export function makeGetContactsManagerParameterType() {
  return createSelector(makeGetContactsManagerPanel(), ({parameters}) => parameters.type);
}

export function makeGetContactsManagerParameters() {
  return createSelector(makeGetContactsManagerPanel(), ({parameters}) => parameters);
}

export function makeGetContactsManagerSearchState() {
  return createSelector(makeGetContactsManagerPanel(), ({searchState}) => searchState);
}

export function makeGetContactsManagerTableState() {
  return createSelector(makeGetContactsManagerPanel(), ({tableState}) => tableState);
}

export function makeGetContactsManagerSidePanelState() {
  return createSelector(makeGetContactsManagerPanel(), ({sidePanelState}) => sidePanelState);
}

export function makeGetContactsManagerCreateContactFormState() {
  return createSelector(makeGetContactsManagerSidePanelState(), (sidePanelState) => {
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return undefined;
    }
    return sidePanelState;
  });
}

export function makeGetContactsManagerFileState() {
  return createSelector(makeGetContactsManagerPanel(), ({fileState}) => fileState);
}

export const getContactsManagerSidePanelState = createSelector(
  getContactsManagerPanel,
  (panel) => panel?.sidePanelState,
);

export const getContactsManagerParameters = createSelector(
  getContactsManagerPanel,
  (panel) => panel?.parameters,
);

export const getContactsManagerTableState = createSelector(
  getContactsManagerPanel,
  (panel) => panel?.tableState,
);

export const getContactsManagerFileState = createSelector(
  getContactsManagerPanel,
  (panel) => panel?.fileState,
);

export function makeGetAccountId() {
  return createSelector([getContactsManagerParameters], (params) => {
    if (
      params?.type === ContactsManagerParametersTypesEnum.ACCOUNTS ||
      params?.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
    ) {
      return params.accountId;
    }
    return undefined;
  });
}

export function makeGetRouteCardGroupId() {
  return createSelector([getContactsManagerParameters], (params) => {
    if (params?.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS) {
      return params.cardGroupId;
    }
    return undefined;
  });
}

export function makeGetRouteCardId() {
  return createSelector([getContactsManagerParameters], (params) => {
    if (
      params?.type === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS ||
      params?.type === ContactsManagerParametersTypesEnum.OTHER_CONTACTS ||
      params?.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS ||
      params?.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS ||
      params?.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
    ) {
      return params.cardId;
    }
    return undefined;
  });
}

export function makeGetCardIdFromParameters() {
  return createSelector([makeGetContactsManagerPanel()], (panel) => {
    const {parameters} = panel;
    if (
      parameters.type === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.OTHER_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.LIST_CONTACTS ||
      parameters.type === ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS
    ) {
      return parameters.cardId;
    }
    return undefined;
  });
}

export function makeGetNamespace() {
  return createSelector([getContactsManagerParameters], (params) => {
    if (params?.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS) {
      return params.namespace;
    }
    return undefined;
  });
}

export function makeGetCurrentRouteType() {
  return createSelector([getContactsManagerParameters], (params) => params?.type);
}

export function makeGetAccountsTableAccountIds() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsAccountsTableState(tableState)) {
      return [];
    }
    return tableState.accountIds;
  });
}

export function makeGetContactsTableCardIds() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsContactsTableState(tableState)) {
      return [];
    }
    return tableState.cardIds;
  });
}

export function makeGetListsTableGroupIds(
  getContactsManagerTableStateParametricSelector: ReturnType<typeof makeGetContactsManagerTableState>,
) {
  return createSelector([getContactsManagerTableStateParametricSelector], (tableState) => {
    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return [];
    }
    return tableState.cardGroupIds;
  });
}

export function makeGetContactsManagerTableOffsetPaginationData(
  getContactsManagerTableStateParametricSelector: ReturnType<typeof makeGetContactsManagerTableState>,
) {
  return createSelector([getContactsManagerTableStateParametricSelector], (tableState) => {
    if (tableStateIsContactsTableState(tableState) || tableStateIsAccountContactsTableState(tableState)) {
      return tableState.paginationData;
    }
    return undefined;
  });
}

export function makeGetContactsManagerTableTokenPaginationData(
  getContactsManagerTableStateParametricSelector: ReturnType<typeof makeGetContactsManagerTableState>,
) {
  return createSelector([getContactsManagerTableStateParametricSelector], (tableState) => {
    if (tableStateIsAccountsTableState(tableState) || tableStateIsListsTableState(tableState)) {
      return tableState.paginationData;
    }
    return undefined;
  });
}

export function makeGetAccountsCSVFileState() {
  return createSelector([getContactsManagerFileState], (fileState) => {
    if (!fileState || !fileStateIsAccountsCSVFile(fileState)) {
      return undefined;
    }
    return fileState;
  });
}

export function makeGetAccountsCSVFileMapping() {
  return createSelector([getContactsManagerFileState], (fileState) => {
    if (!fileState || !fileStateIsAccountsCSVFile(fileState)) {
      return undefined;
    }
    return fileState?.mapping;
  });
}

export function makeGetContactsCSVFileState() {
  return createSelector([makeGetContactsManagerFileState()], (fileState) => {
    if (!fileState || !fileStateIsContactsCsvFile(fileState)) {
      return undefined;
    }
    return fileState;
  });
}

export function makeGetAccountContactsTableCardIds() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsAccountContactsTableState(tableState)) {
      return [];
    }
    return tableState.cardIds;
  });
}

export function makeGetIsInMultiSelectMode() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (
      tableStateIsContactsTableState(tableState) ||
      tableStateIsListsTableState(tableState) ||
      tableStateIsAccountContactsTableState(tableState)
    ) {
      return tableState.isInMultiSelectMode;
    }
    return false;
  });
}

export function makeGetSelectedCardGroupIds() {
  return createSelector(makeGetContactsManagerPanel(), ({tableState}) => {
    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return [];
    }
    return tableState.selectedCardGroupIds;
  });
}

export function makeGetSelectedCardIds() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (
      !tableState ||
      (!tableStateIsContactsTableState(tableState) && !tableStateIsAccountContactsTableState(tableState))
    ) {
      return [];
    }
    return tableState.selectedCardIds;
  });
}
